import React, { useState } from 'react';
import { Upload, Smile, X, Image, Type } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { createCategory } from '../../services/category.service';
import { toast } from 'react-toastify';

const GigCategoryForm = () => {
    const [formData, setFormData] = useState({
        categoryName: '',
        description: '',
        backgroundImage: null,
        iconType: 'text', // 'text' or 'image'
        iconText: '',
        iconImage: null
    });

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [backgroundPreview, setBackgroundPreview] = useState(null);
    const [iconPreview, setIconPreview] = useState(null);

    // Emoji array for selection
    const emojis = [
        '🎨', '💻', '📱', '🌐', '📸', '🎥', '✍️', '📊', '🎵', '🎭',
        '🏗️', '🔧', '⚡', '🚀', '💡', '🎯', '📈', '💼', '🛠️', '📝',
        '🎪', '🖥️', '📺', '🎬', '📷', '🎤', '🎸', '🥁', '🎹',
        '🏀', '⚽', '🎾', '🏈', '🏐', '🏓', '🏸', '🥊', '🥋', '🏊',
        '🍕', '🍔', '🍟', '🌮', '🍝', '🍜', '🍱', '🍣', '🍰', '🧁',
        '🌟', '⭐', '✨', '🔥', '💎', '🏆', '🎖️', '🏅', '👑', '💰',
    ];

    const { mutate: saveCategory, isPending: isSaving } = useMutation({
        mutationFn: () => createCategory({
            name: formData.categoryName,
            description: formData.description,
            icon: formData.iconText || '',
        }),
        onSuccess: () => {
            toast.success('Category created successfully!');
            setFormData({
                categoryName: '',
                description: '',
                backgroundImage: null,
                iconType: 'text',
                iconText: '',
                iconImage: null
            });
            setBackgroundPreview(null);
            setIconPreview(null);
        },
        onError: (error) => {
            toast.error(error?.response?.data?.error || 'Failed to create category');
        }
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleBackgroundImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, backgroundImage: file }));
            const reader = new FileReader();
            reader.onload = (e) => setBackgroundPreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleIconImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({ ...prev, iconImage: file }));
            const reader = new FileReader();
            reader.onload = (e) => setIconPreview(e.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleIconTypeChange = (type) => {
        setFormData(prev => ({
            ...prev,
            iconType: type,
            iconText: type === 'image' ? '' : prev.iconText,
            iconImage: type === 'text' ? null : prev.iconImage
        }));
        if (type === 'text') setIconPreview(null);
    };

    const handleEmojiSelect = (emoji) => {
        setFormData(prev => ({ ...prev, iconText: emoji }));
        setShowEmojiPicker(false);
    };

    const removeBackgroundImage = () => {
        setFormData(prev => ({ ...prev, backgroundImage: null }));
        setBackgroundPreview(null);
    };

    const removeIconImage = () => {
        setFormData(prev => ({ ...prev, iconImage: null }));
        setIconPreview(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.categoryName.trim()) {
            toast.error('Category name is required');
            return;
        }
        saveCategory();
    };


    return (
        <div className="bg-white dark:bg-[#191c22] rounded-lg shadow-sm border border-gray-200 dark:border-[#32353c] p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#4B718F] dark:text-blue-400">Gig Category</h2>
                <button
                    onClick={handleSubmit}
                    disabled={isSaving}
                    className="px-6 py-2 bg-[#174568] text-white rounded-lg hover:bg-[#0f3a4f] transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {isSaving && (
                        <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                    )}
                    {isSaving ? 'Saving...' : 'Save'}
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Category Name */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category Name
                    </label>
                    <input
                        type="text"
                        name="categoryName"
                        value={formData.categoryName}
                        onChange={handleInputChange}
                        placeholder="Enter category name"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B718F] focus:border-transparent"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Description
                    </label>
                    <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="Enter category description"
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B718F] focus:border-transparent resize-none"
                    />
                </div>

                {/* Background Image Upload */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category Background Image
                    </label>
                    {backgroundPreview ? (
                        <div className="relative">
                            <img
                                src={backgroundPreview}
                                alt="Background preview"
                                className="w-full h-48 object-cover rounded-lg border border-gray-300 dark:border-[#32353c]"
                            />
                            <button
                                type="button"
                                onClick={removeBackgroundImage}
                                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <div className="relative border-2 border-dashed border-gray-300 dark:border-[#32353c] rounded-lg p-6 text-center hover:border-[#4B718F] dark:hover:border-blue-500 transition-colors">
                            <Upload className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-2" />
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Click to upload background image</p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">PNG, JPG, GIF up to 10MB</p>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleBackgroundImageUpload}
                                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            />
                        </div>
                    )}
                </div>

                {/* Icon Section */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category Icon
                    </label>
                    
                    {/* Icon Type Selection */}
                    <div className="flex space-x-4 mb-4">
                        <button
                            type="button"
                            onClick={() => handleIconTypeChange('text')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                                formData.iconType === 'text'
                                    ? 'bg-[#4B718F] dark:bg-blue-600 text-white border-[#4B718F] dark:border-blue-600'
                                    : 'bg-white dark:bg-[#1d2026] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-[#32353c] hover:border-[#4B718F] dark:hover:border-blue-500'
                            }`}
                        >
                            <Type className="w-4 h-4" />
                            <span>Text/Emoji</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => handleIconTypeChange('image')}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                                formData.iconType === 'image'
                                    ? 'bg-[#4B718F] dark:bg-blue-600 text-white border-[#4B718F] dark:border-blue-600'
                                    : 'bg-white dark:bg-[#1d2026] text-gray-700 dark:text-gray-300 border-gray-300 dark:border-[#32353c] hover:border-[#4B718F] dark:hover:border-blue-500'
                            }`}
                        >
                            <Image className="w-4 h-4" />
                            <span>Image</span>
                        </button>
                    </div>

                    {/* Text/Emoji Input */}
                    {formData.iconType === 'text' && (
                        <div className="relative">
                            <input
                                type="text"
                                name="iconText"
                                value={formData.iconText}
                                onChange={handleInputChange}
                                placeholder="Enter text or select emoji"
                                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-[#32353c] dark:bg-[#1d2026] dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B718F] focus:border-transparent"
                            />
                            <button
                                type="button"
                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-[#4B718F]"
                            >
                                <Smile className="w-5 h-5" />
                            </button>
                            
                            {/* Emoji Picker */}
                            {showEmojiPicker && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-[#1d2026] border border-gray-300 dark:border-[#32353c] rounded-lg shadow-lg z-10 p-3">
                                    <div className="grid grid-cols-10 gap-2 max-h-48 overflow-y-auto">
                                        {emojis.map((emoji, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                onClick={() => handleEmojiSelect(emoji)}
                                                className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                                            >
                                                {emoji}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Image Upload */}
                    {formData.iconType === 'image' && (
                        <div>
                            {iconPreview ? (
                                <div className="relative inline-block">
                                    <img
                                        src={iconPreview}
                                        alt="Icon preview"
                                        className="w-20 h-20 object-cover rounded-lg border border-gray-300 dark:border-[#32353c]"
                                    />
                                    <button
                                        type="button"
                                        onClick={removeIconImage}
                                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative w-32 h-32 border-2 border-dashed border-gray-300 dark:border-[#32353c] rounded-lg flex flex-col items-center justify-center hover:border-[#4B718F] dark:hover:border-blue-500 transition-colors cursor-pointer">
                                    <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500 mb-1" />
                                    <p className="text-xs text-gray-600 dark:text-gray-400 text-center">Upload Icon</p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500">PNG, JPG</p>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleIconImageUpload}
                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Preview Section */}
                {(formData.categoryName || formData.iconText || iconPreview) && (
                    <div className="border-t dark:border-gray-800 pt-6">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Preview
                        </label>
                        <div 
                            className="relative w-full h-32 rounded-lg flex items-center justify-center text-white overflow-hidden"
                            style={{
                                backgroundImage: backgroundPreview ? `url(${backgroundPreview})` : 'linear-gradient(135deg, #4B718F, #174568)',
                                backgroundSize: 'cover',
                                backgroundPosition: 'center'
                            }}
                        >
                            <div className="absolute inset-0 bg-black bg-opacity-30"></div>
                            <div className="relative flex items-center space-x-3">
                                {formData.iconType === 'text' && formData.iconText && (
                                    <span className="text-2xl">{formData.iconText}</span>
                                )}
                                {formData.iconType === 'image' && iconPreview && (
                                    <img
                                        src={iconPreview}
                                        alt="Category icon"
                                        className="w-8 h-8 object-cover rounded"
                                    />
                                )}
                                {formData.categoryName && (
                                    <span className="text-lg font-semibold">{formData.categoryName}</span>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </form>

            {/* Click outside to close emoji picker */}
            {showEmojiPicker && (
                <div
                    className="fixed inset-0 z-5"
                    onClick={() => setShowEmojiPicker(false)}
                />
            )}
        </div>
    );
};

export default GigCategoryForm;